import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  TextField,
  Typography,
  Box,
  Grid,
  RadioGroup,
  FormControlLabel,
  Radio,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useMediaQuery,
  IconButton,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
} from "@mui/material";
import { Edit, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { PetFormHeader } from "../components/PetFormHeader";
import {
  getPetMedication,
  createMedicationRecord,
  updateMedicationRecord,
  deleteMedicationRecord,
} from "../services/api";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { validateDate, convertFromISO } from "../utils/dateValidation";
import DatePickerField from "../components/DatePickerField";
import { useTranslation } from 'react-i18next';

const Label = ({ children, htmlFor, className, ...props }) => (
  <Typography
    component="label"
    htmlFor={htmlFor}
    className={className}
    variant="body2"
    color="textPrimary"
    {...props}
    sx={{ fontWeight: 700, mb: 1, ...props.sx }}
  >
    {children}
  </Typography>
);

const PetMedication = () => {
  const [isEditMode, setIsEditMode] = useState(false);
  const { petId } = useParams();
  const medicationFormRef = useRef(null);
  const location = useLocation();
  const [petData, setPetData] = useState(location.state?.petData);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [medicationRecords, setMedicationRecords] = useState([]);
  const [showMedicationForm, setShowMedicationForm] = useState(false);
  const [editingMedicationId, setEditingMedicationId] = useState(null);

  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deletingMedicationId, setDeletingMedicationId] = useState(null);

  const [buttonLoading, setButtonLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);
  const [BackColor, setBackColor] = useState("");
  const [UpdateMessage, setUpdateMessage] = useState("");
  const [medicationForm, setMedicationForm] = useState({
    medicationDate: "",
    drugName: "",
    drugPurpose: "",
    drugMethod: "",
    allergy: null,
    drugRemark: "",
  });
  const [dateErrors, setDateErrors] = useState({});

  // Sorting state
  const [sortOrder, setSortOrder] = useState("desc");
  const [sortedRecords, setSortedRecords] = useState([]);

  // Sorting function
  const sortRecordsByDate = (records, order) => {
    return [...records].sort((a, b) => {
      const dateA = new Date(convertFromISO(a.medicationDate));
      const dateB = new Date(convertFromISO(b.medicationDate));

      if (order === "asc") {
        return dateA - dateB;
      } else {
        return dateB - dateA;
      }
    });
  };

  // Handle sorting
  const handleSort = () => {
    const newOrder = sortOrder === "asc" ? "desc" : "asc";
    setSortOrder(newOrder);
    const sorted = sortRecordsByDate(medicationRecords, newOrder);
    setSortedRecords(sorted);
  };

  // Fetch pet data if editing
  useEffect(() => {
    if (location.pathname.includes("edit") && petData) {
      setIsEditMode(true);
      setMedicationRecords(petData.medication);
    }
  }, [location, petData]);

  // Update sorted records when medicationRecords or sortOrder changes
  useEffect(() => {
    const sorted = sortRecordsByDate(medicationRecords, sortOrder);
    setSortedRecords(sorted);
  }, [medicationRecords, sortOrder]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setMedicationForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddNewRecord = () => {
    setShowMedicationForm(true);
    setEditingMedicationId(null);
    setMedicationForm({
      medicationDate: "",
      drugName: "",
      drugPurpose: "",
      drugMethod: "",
      allergy: null,
      drugRemark: "",
    });
  };

  const handleEditRecord = (record) => {
    setShowMedicationForm(true);
    setEditingMedicationId(record._id);
    setMedicationForm({
      medicationDate: convertFromISO(record.medicationDate),
      drugName: record.drugName,
      drugPurpose: record.drugPurpose,
      drugMethod: record.drugMethod,
      allergy: record.allergy,
      drugRemark: record.drugRemark,
    });
    setTimeout(() => {
      medicationFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const handleCancelForm = () => {
    setShowMedicationForm(false);
    setEditingMedicationId(null);
    setMedicationForm({
      medicationDate: "",
      drugName: "",
      drugPurpose: "",
      drugMethod: "",
      allergy: null,
      drugRemark: "",
    });
    setDateErrors({});
  };

  const handleSubmitForm = async () => {
    setButtonLoading(true);
    try {
      // Validate date
      console.log("Validating date:", medicationForm);
      if (medicationForm.medicationDate) {
        const dateValidation = validateDate(medicationForm.medicationDate);
        if (!dateValidation.isValid) {
          setButtonLoading(false);
          setError(true);
          setBackColor("#d32f2f");
          setUpdateMessage(t('ErrorSavingPet'));
          setTimeout(() => {
            setError(false);
          }, 2000);
          setDateErrors({ medicationDate: dateValidation.error });
          return;
        }
      }

      const medicationData = {
        medicationDate: medicationForm.medicationDate,
        drugName: medicationForm.drugName,
        drugPurpose: medicationForm.drugPurpose,
        drugMethod: medicationForm.drugMethod,
        allergy: medicationForm.allergy,
        drugRemark: medicationForm.drugRemark,
      };

      if (editingMedicationId) {
        // Update existing record
        await updateMedicationRecord(
          petId,
          editingMedicationId,
          medicationData
        );
      } else {
        // Create new record
        await createMedicationRecord(petId, medicationData);
      }

      // Refresh data
      const response = await getPetMedication(petId);
      setMedicationRecords(response.data.form.medication);
      setPetData((petData) => ({
        ...petData,
        medication: response.data.form.medication,
      }));
      setButtonLoading(false);
      setSuccess(true);
      setBackColor("#4CAF50");
      setUpdateMessage(t('PetUpdated'));
      setTimeout(() => {
        setSuccess(false);
      }, 2000);

      // Reset form
      handleCancelForm();
    } catch (error) {
      setButtonLoading(false);
      setError(true);
      setBackColor("#d32f2f");
      setUpdateMessage(t('ErrorSavingPet'));
      setTimeout(() => {
        setError(false);
      }, 2000);
      console.error("Error saving medication record:", error);
    }
  };

  const handleMedicationDelete = async () => {
    setDeleteLoading(true);
    try {
      await deleteMedicationRecord(petId, deletingMedicationId);

      // Refresh data
      const response = await getPetMedication(petId);
      setMedicationRecords(response.data.form.medication);
      setPetData((petData) => ({
        ...petData,
        medication: response.data.form.medication,
      }));
      setShowMedicationForm(false);
      setSuccess(true);
      setBackColor("#4CAF50");
      setUpdateMessage(t('PetUpdated'));
      setTimeout(() => {
        setSuccess(false);
      }, 2000);
    } catch (error) {
      setError(true);
      setBackColor("#d32f2f");
      setUpdateMessage(t('ErrorSavingPet'));
      setTimeout(() => {
        setError(false);
      }, 2000);
      console.error("Error deleting medication record:", error);
    } finally {
      setDeleteLoading(false);
      setShowDeleteModal(false);
    }

  };
  const isMobile = useMediaQuery("(max-width:900px)");

  return (
    <Box
      sx={{
        backgroundColor: "#FCF8F3",
        minHeight: "100vh",
        pt: isMobile ? 12 : 16,
      }}
    >
      <Navbar />
      <Box
        sx={{
          maxWidth: 1440,
          mx: "auto",
          mb: { xs: 5, md: 10 },
          px: { xs: 2, sm: 3, md: 0 },
        }}
      >
        <PetFormHeader
          currentPageId="medication"
          isEditMode={isEditMode}
          petId={isEditMode ? petId : null}
          handlePageNavigation={() => { }}
          petData={petData}
        />
        <Box component="form">
          <Typography fontWeight={700} sx={{ fontSize: 18, mb: 4, px: 3 }}>
            {t('MedicationRecords')}
            <Button
              onClick={handleAddNewRecord}
              disabled={showMedicationForm}
              sx={{
                ml: 2,
                fontWeight: 700,
                minWidth: 120,
                "&:hover": {
                  backgroundColor: "black",
                  color: "white",
                },
              }}
              variant="outlined"
              color="text.secondary"
            >
              + {t('AddNewRecord')}
            </Button>
          </Typography>
          {!showMedicationForm && petData.medication.length === 0 && (
            <Typography fontWeight={700} sx={{ color: "#969696", fontSize: 18, p: 3, display: "flex", justifyContent: "center" }}>{t("NoMedicationRecords")}</Typography>
          )}
          {/* Medication Records Table */}
          {medicationRecords.length > 0 && (
            <Box sx={{ px: 3, mb: 4 }}>
              {/* Desktop Table View */}
              <Box sx={{ display: { xs: "none", md: "block" } }}>
                <TableContainer
                  component={Paper}
                  sx={{
                    boxShadow: 1,
                  }}
                >
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                        <TableCell
                          sx={{
                            fontWeight: 700,
                            cursor: "pointer",
                            userSelect: "none",
                            "&:hover": { backgroundColor: "#e8e8e8" },
                          }}
                          onClick={handleSort}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            {t('MedicationDate')}
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                ml: 0.5,
                              }}
                            >
                              {sortOrder === "asc" ? (
                                <ArrowUp size={16} />
                              ) : (
                                <ArrowDown size={16} />
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{t('drugName')}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{t('drugPurpose')}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{t('drugMethod')}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{t('allergy')}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{t('Remarks')}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedRecords.map((record, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            {convertFromISO(record.medicationDate)}
                          </TableCell>
                          <TableCell>{record.drugName}</TableCell>
                          <TableCell>{record.drugPurpose}</TableCell>
                          <TableCell>{record.drugMethod}</TableCell>
                          <TableCell>
                            {record.allergy ? t('Yes') : t('No')}
                          </TableCell>
                          <TableCell>{record.drugRemark}</TableCell>
                          <TableCell>
                            <IconButton
                              onClick={() => handleEditRecord(record)}
                              size="small"
                              sx={{ mr: 1 }}
                            >
                              <Edit size={16} />
                            </IconButton>
                            <IconButton
                              onClick={() => { setDeletingMedicationId(record._id); setShowDeleteModal(true); }}
                              size="small"
                              color="error"
                            >
                              <Trash2 size={16} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              {/* Mobile Simple Text View */}
              <Box sx={{ display: { xs: "block", md: "none" } }}>
                <Box
                  sx={{ display: "flex", alignItems: "center", mb: 2, px: 2 }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 700, color: "#666" }}
                  >
                    {t('sortby')}
                  </Typography>
                  <Button
                    onClick={handleSort}
                    size="small"
                    sx={{
                      ml: 1,
                      color: "#FFB60C",
                      fontWeight: 700,
                      textTransform: "none",
                      "&:hover": { backgroundColor: "rgba(255,182,12,0.08)" },
                    }}
                  >
                    {t('MedicationDate')} {sortOrder === "asc" ? "▲" : "▼"}
                  </Button>
                </Box>
                {sortedRecords.map((record, index) => (
                  <Box
                    key={index}
                    sx={{
                      mb: 4,
                      p: 3,
                      backgroundColor: "white",
                      borderRadius: 2,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        mb: 3,
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 700, fontSize: 16, color: "#333" }}
                      >
                        {t('MedicationRecords')}
                      </Typography>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <IconButton
                          onClick={() => handleEditRecord(record)}
                          size="small"
                          sx={{ color: "#FFB60C" }}
                        >
                          <Edit size={20} />
                        </IconButton>
                        <IconButton
                          onClick={() => { setDeletingMedicationId(record._id); setShowDeleteModal(true); }}
                          size="small"
                          sx={{ color: "#A0A0A0" }}
                        >
                          <Trash2 size={20} />
                        </IconButton>
                      </Box>
                    </Box>

                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                    >
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, color: "#666", mb: 0.5 }}
                        >
                          {t('MedicationDate')}
                        </Typography>
                        <Typography variant="body1" sx={{ color: "#333" }}>
                          {convertFromISO(record.medicationDate) || "-"}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, color: "#666", mb: 0.5 }}
                        >
                          {t('drugName')}
                        </Typography>
                        <Typography variant="body1" sx={{ color: "#333" }}>
                          {record.drugName || "-"}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, color: "#666", mb: 0.5 }}
                        >
                          {t('drugPurpose')}
                        </Typography>
                        <Typography variant="body1" sx={{ color: "#333" }}>
                          {record.drugPurpose || "-"}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, color: "#666", mb: 0.5 }}
                        >
                          {t('drugMethod')}
                        </Typography>
                        <Typography variant="body1" sx={{ color: "#333" }}>
                          {record.drugMethod || "-"}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, color: "#666", mb: 0.5 }}
                        >
                          {t('allergy')}
                        </Typography>
                        <Typography variant="body1" sx={{ color: "#333" }}>
                          {record.allergy ? t('Yes') : t('No')}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, color: "#666", mb: 0.5 }}
                        >
                          {t('Remarks')}
                        </Typography>
                        <Typography variant="body1" sx={{ color: "#333" }}>
                          {record.drugRemark || "-"}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* Medication Form */}
          {showMedicationForm && (
            <>
              <Grid
                ref={medicationFormRef}
                container
                spacing={3}
                justifyContent="space-between"
                sx={{ px: 3, backgroundColor: "#FFF2E2", py: 3 }}
              >
                <Grid item xs={12} md={4} sx={{ maxWidth: 350, width: "100%" }}>
                  <Label htmlFor="medicationDate">{t('MedicationDate')}</Label>
                  <DatePickerField
                    id="medicationDate"
                    name="medicationDate"
                    placeholder="DD/MM/YYYY"
                    value={medicationForm.medicationDate}
                    onChange={(value) =>
                      setMedicationForm((prev) => ({
                        ...prev,
                        medicationDate: value,
                      }))
                    }
                    onError={(error) =>
                      setDateErrors({ ...dateErrors, medicationDate: error })
                    }
                    error={!!dateErrors.medicationDate}
                    helperText={dateErrors.medicationDate}
                  />
                </Grid>
                <Grid item xs={12} md={4} sx={{ maxWidth: 350, width: "100%" }}>
                  <Label htmlFor="drugName">{t('drugName')}</Label>
                  <TextField
                    id="drugName"
                    name="drugName"
                    placeholder={t('drugName')}
                    value={medicationForm.drugName}
                    onChange={handleFormChange}
                    fullWidth
                    variant="outlined"
                    sx={{
                      backgroundColor: "white",
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "white",
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4} sx={{ maxWidth: 350, width: "100%" }}>
                  <Label htmlFor="drugPurpose">{t('drugPurpose')}</Label>
                  <TextField
                    id="drugPurpose"
                    name="drugPurpose"
                    placeholder={t('drugPurpose')}
                    value={medicationForm.drugPurpose}
                    onChange={handleFormChange}
                    fullWidth
                    variant="outlined"
                    sx={{
                      backgroundColor: "white",
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "white",
                      },
                    }}
                  />
                </Grid>
              </Grid>

              <Grid
                container
                spacing={3}
                justifyContent="space-between"
                sx={{ px: 3, backgroundColor: "#FFF2E2", }}
              >
                <Grid item xs={12} md={4} sx={{ maxWidth: 350, width: "100%" }}>
                  <Label htmlFor="drugMethod">{t('drugMethod')}</Label>
                  <TextField
                    id="drugMethod"
                    name="drugMethod"
                    placeholder={t('drugMethod')}
                    value={medicationForm.drugMethod}
                    onChange={handleFormChange}
                    fullWidth
                    variant="outlined"
                    sx={{
                      backgroundColor: "white",
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "white",
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4} sx={{ maxWidth: 350, width: "100%" }}>
                  <Label>{t('allergy')}</Label>
                  <RadioGroup
                    row
                    value={medicationForm.allergy ? "true" : "false"}
                    onChange={(e) =>
                      setMedicationForm((prev) => ({
                        ...prev,
                        allergy: e.target.value === "true",
                      }))
                    }
                  >
                    <FormControlLabel
                      value="true"
                      control={<Radio />}
                      label={t('Yes')}
                    />
                    <FormControlLabel
                      value="false"
                      control={<Radio />}
                      label={t('No')}
                    />
                  </RadioGroup>
                </Grid>
                <Grid item xs={12} md={4} sx={{ maxWidth: 350, width: "100%" }}>
                  <Label htmlFor="drugRemark">{t('Remarks')}</Label>
                  <TextField
                    id="drugRemark"
                    name="drugRemark"
                    placeholder={t('Remarks')}
                    value={medicationForm.drugRemark}
                    onChange={handleFormChange}
                    fullWidth
                    variant="outlined"
                    sx={{
                      backgroundColor: "white",
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "white",
                      },
                    }}
                  />
                </Grid>
              </Grid>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: { xs: "flex-start", md: "flex-end" },
                  gap: 2,
                  px: 3,
                  backgroundColor: "#FFF2E2", py: 3
                }}
              >
                <Button
                  type="submit"
                  variant="contained"
                  disabled={buttonLoading}
                  sx={{
                    backgroundColor: "#FFB60C",
                    color: "#050505",
                    minWidth: 120,
                    "&:hover": { backgroundColor: "#FFB60C" },
                    "&:disabled": { background: "rgba(255, 182, 12, 0.5)" },
                    cursor: buttonLoading ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "10px",
                  }}
                  onClick={handleSubmitForm}
                >
                  {t('Save')}
                  {buttonLoading && (
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                      }}
                    >
                      <CircularProgress size={15} color="inherit" />
                    </span>
                  )}
                </Button>
                <Button
                  variant="text"
                  color="inherit"
                  sx={{ color: "#A0A0A0", minWidth: 120, fontWeight: 700 }}
                  onClick={handleCancelForm}
                >
                  {t('cancel')}
                </Button>
              </Box>
            </>
          )}

          {/* Bottom Buttons */}
          <Box
            sx={{
              display: "flex",
              justifyContent: { xs: "center", md: "flex-end" },
              mt: { xs: 5, md: 10 },
              mr: 3,
              gap: 2,
            }}
          >
            <Button
              type="button"
              variant="outlined"
              color="text.secondary"
              onClick={() => navigate("/pet-management")}
              sx={{
                minWidth: 120,
                "&:hover": {
                  backgroundColor: "black",
                  color: "white",
                },
              }}
            >
              {t('BackToHome')}
            </Button>
          </Box>
        </Box>
      </Box>
      <Footer />
      <Box
        sx={{
          position: "fixed",
          bottom: 20,
          right: (success || error) ? 20 : -400,
          backgroundColor: BackColor,
          color: "#fff",
          padding: "16px 24px",
          borderRadius: 2,
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          zIndex: 9999,
          minWidth: 250,
          display: "flex",
          alignItems: "center",
          gap: 2,
          transition: (success || error)
            ? "right 0.4s ease-out"
            : "right 0.4s ease-in",
        }}
      >
        <Box
          sx={{
            width: 24,
            height: 24,
            borderRadius: "50%",
            backgroundColor: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {error ? (
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke={BackColor}
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          ) : (
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M20 6L9 17L4 12"
                stroke={BackColor}
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </Box>
        <Typography fontWeight={600} fontSize={isMobile ? 14 : 16}>
          {UpdateMessage}
        </Typography>
      </Box>
      <Dialog
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        PaperProps={{
          sx: {
            borderRadius: 4,
            maxWidth: 400,
            width: "100%",
            p: 3,
            m: 2,
            boxSizing: "border-box",
            textAlign: "center",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            fontWeight: 700,
            fontSize: 20,
            justifyContent: "center",
            gap: 1,
          }}
        >

          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10.2947 3.86118L1.82466 18.0012C1.65003 18.3036 1.55763 18.6465 1.55665 18.9957C1.55567 19.3449 1.64615 19.6883 1.81909 19.9917C1.99202 20.2951 2.24139 20.5479 2.54237 20.725C2.84335 20.9021 3.18546 20.9973 3.53466 21.0012H20.4747C20.8239 20.9973 21.166 20.9021 21.467 20.725C21.7679 20.5479 22.0173 20.2951 22.1902 19.9917C22.3632 19.6883 22.4536 19.3449 22.4527 18.9957C22.4517 18.6465 22.3593 18.3036 22.1847 18.0012L13.7147 3.86118C13.5364 3.56729 13.2854 3.3243 12.9859 3.15567C12.6863 2.98703 12.3484 2.89844 12.0047 2.89844C11.6609 2.89844 11.323 2.98703 11.0235 3.15567C10.7239 3.3243 10.4729 3.56729 10.2947 3.86118Z" stroke="#FF1A1A" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M12 9V13" stroke="#FF1A1A" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M12 17H12.008" stroke="#FF1A1A" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
          </svg>

          {t('ConfirmDeleteMedication')}
        </Box>
        <DialogContent sx={{ pt: 0, pb: 0 }}>
          <Typography
            variant="body1"
            sx={{ mb: 3, textAlign: "center", fontSize: 16 }}
          >
            {t('Systemwilldelete')}
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            flexDirection: "column",
            "& > :not(style) ~ :not(style)": {
              marginLeft: 0,
            },
          }}
        >
          <Button
            fullWidth
            variant="contained"
            sx={{
              backgroundColor: "#FF1A1A",
              color: "#050505",
              fontWeight: 700,
              fontSize: 16,
              borderRadius: 2,
              py: 1,
              mb: 1,
              textAlign: "center",
              textTransform: 'none',
              "&:hover": { backgroundColor: "#d63e3eff" },
              cursor: deleteLoading ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "10px",
            }}
            onClick={handleMedicationDelete}
          >
            {t('Delete')}
            {deleteLoading && (
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                }}
              >
                <CircularProgress size={15} color="inherit" />
              </span>
            )}
          </Button>
          <Button
            fullWidth
            variant="text"
            sx={{
              color: "#B0B0B0",
              fontWeight: 700,
              fontSize: 16,
              borderRadius: 2,
              py: 1,
              textAlign: "center",
              textTransform: 'none',
            }}
            onClick={() => setShowDeleteModal(false)}
          >
            {t('cancel')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PetMedication;
